import React from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { FieldArray } from 'formik';

import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import startCase from 'lodash/startCase';
import DeleteSharpIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PageSelector from './elements/PageSelector';
import Typography from '@mui/material/Typography';
import ArrowRightOutlined from '@mui/icons-material/ArrowRightOutlined';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Close from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import Popover from '@mui/material/Popover';
import { ReactComponent as NoItem } from 'assets/icon/no-item.svg';
import IconButton from '@mui/material/IconButton';
import { LIST_PAGE_BAU, LIST_PAGE_CUSTOMER_PORTAL, listDirection, SLOT_KEY } from 'constants/FeaturesContants';
import Add from '@mui/icons-material/Add';
import ArrowCircleUp from '@mui/icons-material/ArrowCircleUp'
import ArrowCircleDown from '@mui/icons-material/ArrowCircleDown'
import AlertConfirmDialog from 'components/AlertConfirmDialog';
import AppGridContainer from '@tenant/core/AppGridContainer';
import AppGlossary from '@tenant/core/App/Common/AppGlossary';

import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

const FeatureStepWebMarketPlace = ({ values, setFieldValue, keyField = 'PageSlots' }) => {
  const LIST_PAGE = keyField === 'PageSlotsPortal' ? LIST_PAGE_CUSTOMER_PORTAL : LIST_PAGE_BAU;

  const isValidFeatureItem = (feature) => {
    return feature.source && feature.featureName;
  };

  const [currentListPagesAvailable, setCurrentListPagesAvailable] =
    React.useState(cloneDeep(LIST_PAGE));

  React.useEffect(() => {
    const selectedPages = values[keyField]?.map((item) => item.matchRegex) || [];
    setCurrentListPagesAvailable(
      LIST_PAGE.filter((item) => !selectedPages.includes(item.matchRegex)),
    );
  }, [values[keyField]]);

  const getListSlotsByPage = (pageKey, pageSelectedSlot) => {
    const pageSelectedSlotKey =
      pageSelectedSlot?.map((item) => item.type) ?? [];
    const result =
      LIST_PAGE.find((item) => pageKey === item.matchRegex)?.slots ?? [];

    return result
      .filter((item) => !pageSelectedSlotKey.includes(item))
      .map((item) => ({
        type: item,
        label: startCase(item),
      }));
  };

  const getPageInfo = (pageKey, keyField) => {
    const result = LIST_PAGE.find((item) => item.matchRegex === pageKey);

    if (result && keyField) return get(result, keyField);
    return result;
  };

  const getCurrentPageSlotDescriptionText = (slots) => {
    const defaultText = 'No slots selected';

    if (!slots?.length) return defaultText;

    const result = [];

    slots.map((slot) => {
      const featuresLength = slot.features?.filter?.((feature) =>
        isValidFeatureItem(feature),
      )?.length;

      if (featuresLength > 0) result.push(`${featuresLength} ${slot.label}`);
    });

    return result.join(', ') || defaultText;
  };

  const DEFAULT_FEATURE_ITEM_MODEL = {
    source: '',
    featureName: '',
    parentKey: '',
    location: listDirection[0].key,
    locationIndex: 0,
    isInitialDownload: false,
  };
  const pageItemObj = { matchRegex: '', slots: [] };

  // Modal
  const [open, setOpen] = React.useState(false);
  const [currentPageIndex, setCurrentPageIndex] = React.useState(null);
  const handleClickOpenModal = (index) => {
    setOpen(true);
    setCurrentPageIndex(index);

    setFieldValue(`${keyField}Temp`, values[keyField]);
  };
  const handleCloseModal = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }

    setOpen(false);
    setCurrentPageIndex(null);

    setFieldValue(`${keyField}Temp`, null);
  };

  // Popper
  const [anchorEl, setAnchorEl] = React.useState(null);
  const handleClickOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClosePopover = () => {
    setAnchorEl(null);
  };
  const openPopover = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const [openConfirmDelete, setOpenConfirmDelete] = React.useState(false);
  const [currentAction, setCurrentAction] = React.useState(null);

  const handleClickOpenConfirm = (action, ...args) => {
    setOpenConfirmDelete(true);
    setCurrentAction({ action, args });
  };

  const handleClose = () => {
    setOpenConfirmDelete(false);
    setCurrentAction(null);
  };

  const handleConfirm = () => {
    if (currentAction) {
      setCurrentAction(null);
      const { action, args } = currentAction;
      if (typeof action === 'function') action(...args);
    }
    setOpenConfirmDelete(false);
  };

  //
  const getFeatureNamePlaceholder = (slotObj) => {
    const type = slotObj?.type

    if (type === SLOT_KEY.SLOT_V_TABLE_COLUMN) {
      return 'Type table column name'
    } else if (type === SLOT_KEY.SLOT_SCRIPTS || type?.includes('slotStaticScript')) {
      return 'Type script name'
    }

    return 'Type unique feature name'
  }

  const isDisabledDirection = (slotObj) => {
    const type = slotObj?.type

    if ([SLOT_KEY.SLOT_V_TABLE_COLUMN, SLOT_KEY.SLOT_SCRIPTS].includes(type) || type?.includes('slotStaticScript')) {
      return true
    }
    return false
  }

  const getSlotNoteText = (pageKey, slotKey) => {
    try {
      const currentPageInfo = getPageInfo(pageKey, 'description')

      return currentPageInfo[slotKey] || ''
    } catch (e) {
      return ''
    }
  }

  const getSlotOptions = (pageKey, slotKey) => {
    try {
      const currentPageInfo = getPageInfo(pageKey, 'options')

      return currentPageInfo[slotKey] || []
    } catch (e) {
      return []
    }
  }

  const getSlotParentKeys = (pageKey, slotKey) => {
    try {
      const currentPageInfo = getPageInfo(pageKey, 'parentKeys')

      return currentPageInfo[slotKey] || []
    } catch (e) {
      return []
    }
  }

  const getSlotReplaceElements = (pageKey, slotKey) => {
    try {
      const currentPageInfo = getPageInfo(pageKey, 'replaceElements')

      if (!currentPageInfo) return []

      return currentPageInfo[slotKey] || []
    } catch (e) {
      return []
    }
  }

  // Position
  function moveElement(array, index, direction) {
    // Ensure the direction is valid
    if (direction !== 'up' && direction !== 'down') {
      console.log('Invalid direction. Please use "up" or "down".');
      return array;
    }

    // Check if the index is within bounds
    if (index < 0 || index >= array.length) {
      console.log('Index is out of bounds.');
      return array;
    }

    // Swap the element based on direction
    if (direction === 'up' && index > 0) {
      // Move element up (swap with previous element)
      [array[index], array[index - 1]] = [array[index - 1], array[index]];
    } else if (direction === 'down' && index < array.length - 1) {
      // Move element down (swap with next element)
      [array[index], array[index + 1]] = [array[index + 1], array[index]];
    }

    return array;
  }

  return (
    <>
      <AlertConfirmDialog
        open={openConfirmDelete}
        title='Confirm Action'
        message='Are you sure you want to proceed?'
        onConfirm={handleConfirm}
        onCancel={handleClose}
      />
      <AppGridContainer>
        {(keyField === 'PageSlotsPortal') ? (
          <Grid item xs={12} sm={12} md={12}>
            <Tooltip
              placement='right'
              title='This field contains an embedded link to the Customer Portal for quick navigation.'
            >
              <Typography variant='h5' mb={2}>
                Embedded Source URL
              </Typography>
            </Tooltip>
            <TextField
              placeholder='Enter source url'
              value={values.PortalEmbeddedUrl ?? ''}
              onChange={async (e) => {
                setFieldValue('PortalEmbeddedUrl', e.target.value);
              }}
              onBlur={() => {
                setFieldValue('PortalEmbeddedUrl', values.PortalEmbeddedUrl?.trim());
              }}
              fullWidth
            />
          </Grid>
        ) : (
          <Grid item xs={12} sm={12} md={12}>
            <Tooltip
              placement='right'
              title='This field contains an embedded link to the BAU for quick navigation.'
            >
              <Typography variant='h5' mb={2}>
                Embedded Source URL
              </Typography>
            </Tooltip>
            <TextField
              placeholder='Enter source url'
              value={values.EmbeddedUrl ?? ''}
              onChange={async (e) => {
                setFieldValue('EmbeddedUrl', e.target.value);
              }}
              onBlur={() => {
                setFieldValue('EmbeddedUrl', values.EmbeddedUrl?.trim());
              }}
              fullWidth
            />
          </Grid>
        )}

        <FeatureConfigurationHeader
          values={values}
          currentListPagesAvailable={currentListPagesAvailable}
          pageItemObj={pageItemObj}
          keyField={keyField}
        />

        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          sx={{
            mt: 4,
          }}
        >
          <FieldArray name={keyField}>
            {({ remove: removePage }) => (
              <>
                {values[keyField]?.length > 0 ? (
                  values[keyField].map((itempage, index) => (
                    <Box
                      key={index}
                      mb={4}
                      sx={{
                        '&:hover': {
                          boxShadow: 1,
                        },
                        cursor: 'pointer',
                        border: 1,
                        borderColor: '#8080804a',
                        borderRadius: 2,
                        p: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 4,
                      }}
                      onClick={() => {
                        handleClickOpenModal(index);
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Typography>
                          {getPageInfo(
                            get(values, `${keyField}.${index}.matchRegex`),
                            'name',
                          ) || get(values, `${keyField}.${index}.matchRegex`)}
                        </Typography>
                        <ArrowRightOutlined />
                        <Typography variant='caption'>
                          {getCurrentPageSlotDescriptionText(
                            get(values, `${keyField}.${index}.slots`),
                          )}
                        </Typography>
                      </Box>

                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClickOpenConfirm(removePage, index);
                        }}
                      >
                        <DeleteSharpIcon size='small' sx={{ color: 'grey' }} />
                      </IconButton>
                    </Box>
                  ))
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: '200px',
                      border: '1px solid #E0E0E0',
                      borderRadius: '12px',
                    }}
                  >
                    <NoItem />

                    <Typography sx={{ marginTop: 2 }} variant='caption'>
                      No item displayed
                    </Typography>

                    <Box sx={{ marginTop: 2 }}>
                      <FieldArray name={keyField}>
                        {({ push: pushPage }) => (
                          <PageSelector
                            options={currentListPagesAvailable}
                            onChange={(value) => {
                              pushPage({
                                ...pageItemObj,
                                matchRegex: value.matchRegex,
                              });
                            }}
                          />
                        )}
                      </FieldArray>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </FieldArray>
        </Grid>

        <Dialog
          open={open}
          onClose={handleCloseModal}
          maxWidth={false}
          onBackdropClick={(e) => e.stopPropagation()} // Prevent closing on backdrop click
        >
          <DialogContent sx={{ minWidth: 900 }}>
            <Typography variant={'h3'}>
              {' '}
              {getPageInfo(
                get(values, `${keyField}Temp.${currentPageIndex}.matchRegex`),
                'name',
              ) || get(values, `${keyField}Temp.${currentPageIndex}.matchRegex`)}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                mt: 4,
              }}
            >
              <img
                src={getPageInfo(
                  get(values, `${keyField}Temp.${currentPageIndex}.matchRegex`),
                  'image',
                )}
                style={{ maxHeight: '400px' }}
              />

              <Typography variant='p' sx={{ textAlign: 'center' }}>
                Select the slot type to create appropriate new elements (tabs,
                buttons, or sections, etc). These elements will be displayed
                according to the guideline above.
              </Typography>
            </Box>

            <FieldArray name={`${keyField}Temp.${currentPageIndex}.slots`}>
              {({ remove: removeSlot, push: pushSlot }) => (
                <>
                  {get(
                    values,
                    `${keyField}Temp.${currentPageIndex}.slots.length`,
                  ) > 0 ? (
                    get(
                      values,
                      `${keyField}Temp.${currentPageIndex}.slots`,
                      [],
                    ).map((itemSlot, indexSlot) => (
                      <Box
                        key={indexSlot}
                        sx={{
                          mt: 2,
                          border: 1,
                          borderColor: '#8080804a',
                          borderRadius: 2,
                          p: 4,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 4,
                            mb: 2,
                          }}
                        >
                          <Typography>
                            <b>
                              {get(
                                values,
                                `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].label`,
                              )}
                            </b>
                          </Typography>

                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              handleClickOpenConfirm(removeSlot, indexSlot);
                            }}
                          >
                            <DeleteSharpIcon
                              size='small'
                              sx={{ color: 'grey' }}
                            />
                          </IconButton>
                        </Box>

                        <FieldArray
                          name={`${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features`}
                        >
                          {({ remove: removeFeature, push: pushFeature }) => (
                            <>
                              <Typography>
                                {getSlotNoteText(get(
                                  values,
                                  `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                ), get(
                                  values,
                                  `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                ))}
                              </Typography>

                              {get(
                                values,
                                `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features.length`,
                              ) > 0 ? (
                                get(
                                  values,
                                  `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features`,
                                  [],
                                ).map((itemFeature, indexFeature) => (
                                  <Box
                                    key={indexFeature}
                                    style={{ marginBottom: '12px' }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'center',
                                      }}
                                    >
                                      {!isDisabledDirection(get(
                                        values,
                                        `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}]`,
                                      ) ?? '') ? <FormControl fullWidth>
                                        <Select
                                          disabled={isDisabledDirection(get(
                                            values,
                                            `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}]`,
                                          ) ?? '')}
                                          labelId='demo-simple-select-label'
                                          size='small'
                                          value={
                                            get(
                                              values,
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].location`,
                                            ) ?? ''
                                          }
                                          onChange={async (e) => {
                                            setFieldValue(
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].location`,
                                              e.target.value,
                                            );
                                            setFieldValue(
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].replaceElement`,
                                              ""
                                            );

                                          }}
                                        >
                                          {listDirection.filter(e => !getSlotReplaceElements(get(
                                            values,
                                            `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                          ), get(
                                            values,
                                            `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                          ))?.length ? e.key !== 'replace-element' : true).map((itemLocation) => {
                                            return (
                                              <MenuItem
                                                key={itemLocation.key}
                                                value={itemLocation.key}
                                              >
                                                {itemLocation.label}
                                              </MenuItem>
                                            );
                                          })}
                                        </Select>
                                      </FormControl> : <></>}

                                      {(!isDisabledDirection(get(
                                        values,
                                        `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}]`,
                                      )) && (['custom', 'custom-end', 'replace-element'].includes(get(
                                        values,
                                        `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].location`,
                                      )))) ? <FormControl fullWidth>
                                        <TextField
                                          min={0}
                                          type="number"
                                          size='small'
                                          placeholder='Type custom index'
                                          value={
                                            get(
                                              values,
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].locationIndex`,
                                            ) ?? ''
                                          }
                                          onChange={async (e) => {
                                            setFieldValue(
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].locationIndex`,
                                              e.target.value,
                                            );
                                          }}
                                          fullWidth
                                        />
                                      </FormControl> : <></>}

                                      {getSlotParentKeys(get(
                                        values,
                                        `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                      ), get(
                                        values,
                                        `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                      ))?.length ? <FormControl fullWidth>
                                        <Autocomplete
                                          value={
                                            get(
                                              values,
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].parentKey`,
                                            ) ?? ''
                                          }
                                          onChange={async (e, value) => {
                                            setFieldValue(
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].parentKey`,
                                              value?.key,
                                            );
                                          }}
                                          options={getSlotParentKeys(get(
                                            values,
                                            `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                          ), get(
                                            values,
                                            `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                          ))}
                                          renderInput={(params) => {

                                            if (params?.inputProps?.value && typeof params?.inputProps?.value === 'string') {
                                              params.inputProps.value = getSlotParentKeys(get(
                                                values,
                                                `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                              ), get(
                                                values,
                                                `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                              )).find(itemParentKey => itemParentKey.key === params.inputProps.value)?.label || params.inputProps.value
                                            }

                                            return <TextField
                                              {...params}
                                              size='small'
                                              placeholder='Select a parent key'
                                            />
                                          }}
                                          isOptionEqualToValue={(option, value) => {
                                            return option?.key === value
                                          }
                                          }
                                        ></Autocomplete>
                                      </FormControl> : <></>
                                      }

                                      {(getSlotReplaceElements(get(
                                        values,
                                        `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                      ), get(
                                        values,
                                        `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                      ))?.length && get(
                                        values,
                                        `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].location`,
                                      ) === 'replace-element') ? <FormControl fullWidth>
                                        <Autocomplete
                                          value={
                                            get(
                                              values,
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].replaceElement`,
                                            ) ?? ''
                                          }
                                          onChange={(_, value) => {
                                            setFieldValue(
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].replaceElement`,
                                              value?.key,
                                            );
                                          }}
                                          options={getSlotReplaceElements(get(
                                            values,
                                            `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                          ), get(
                                            values,
                                            `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                          ))}
                                          renderInput={(params) => {
                                            if (params?.inputProps?.value && typeof params?.inputProps?.value === 'string') {
                                              params.inputProps.value = getSlotReplaceElements(get(
                                                values,
                                                `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                              ), get(
                                                values,
                                                `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                              )).find(item => item.key === params.inputProps.value)?.label || params.inputProps.value
                                            }
                                            return <TextField
                                              {...params}
                                              size='small'
                                              placeholder='Select element to replace'
                                            />
                                          }}
                                          isOptionEqualToValue={(option, value) => {
                                            return option?.key === value
                                          }}
                                        ></Autocomplete>
                                      </FormControl> : <></>}

                                      <FormControl fullWidth>
                                        {getSlotOptions(get(
                                          values,
                                          `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                        ), get(
                                          values,
                                          `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                        ))?.length ?
                                          <Autocomplete
                                            value={
                                              get(
                                                values,
                                                `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].featureName`,
                                              ) ?? ''
                                            }
                                            onChange={async (e, value) => {
                                              setFieldValue(
                                                `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].featureName`,
                                                value?.key,
                                              );
                                            }}
                                            options={getSlotOptions(get(
                                              values,
                                              `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                            ), get(
                                              values,
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                            ))}
                                            renderInput={(params) => {
                                              if (params?.inputProps?.value && typeof params?.inputProps?.value === 'string') {
                                                params.inputProps.value = getSlotOptions(get(
                                                  values,
                                                  `${keyField}Temp.${currentPageIndex}.matchRegex`,
                                                ), get(
                                                  values,
                                                  `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].type`,
                                                )).find(itemOption => itemOption.key === params.inputProps.value)?.label || params.inputProps.value
                                              }

                                              return <TextField
                                                {...params}
                                                size='small'
                                                placeholder='Select a feature name'
                                              />

                                            }}
                                            isOptionEqualToValue={(option, value) => {
                                              return option?.key === value
                                            }
                                            }
                                          ></Autocomplete>
                                          : <TextField
                                            size='small'
                                            placeholder={getFeatureNamePlaceholder(get(
                                              values,
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}]`,
                                            ) ?? '')}
                                            value={
                                              get(
                                                values,
                                                `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].featureName`,
                                              ) ?? ''
                                            }
                                            onChange={async (e) => {
                                              setFieldValue(
                                                `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].featureName`,
                                                e.target.value,
                                              );
                                            }}
                                            fullWidth
                                          />
                                        }
                                      </FormControl>

                                      <FormControl fullWidth>
                                        <TextField
                                          size='small'
                                          placeholder='Paste the source here'
                                          value={
                                            get(
                                              values,
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].source`,
                                            ) ?? ''
                                          }
                                          onChange={async (e) => {
                                            setFieldValue(
                                              `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].source`,
                                              e.target.value,
                                            );
                                          }}
                                          fullWidth
                                        />
                                      </FormControl>

                                      <Box sx={{ minWidth: '64px' }}>
                                        <FormControl >
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={get(
                                                  values,
                                                  `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].isInitialDownload`,
                                                ) ?? false}
                                                onChange={async (e) => {
                                                  setFieldValue(
                                                    `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}].isInitialDownload`,
                                                    e.target.checked,
                                                  );
                                                }} />
                                            }
                                            label="Initial"
                                          />
                                        </FormControl>
                                      </Box>

                                      {get(
                                        values,
                                        `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features.length`,
                                      ) > 1 && (
                                          <Box style={{ display: 'flex' }}>
                                            <IconButton
                                              title="Move up"
                                              disabled={indexFeature === 0}
                                              onClick={() => {
                                                const resultArray = moveElement(get(
                                                  values,
                                                  `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features`,
                                                ), indexFeature, 'up')
                                                setFieldValue(
                                                  `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features`,
                                                  resultArray,
                                                );
                                              }}
                                            >
                                              <ArrowCircleUp />
                                            </IconButton>

                                            <IconButton
                                              title="Move down"
                                              disabled={indexFeature + 1 === get(
                                                values,
                                                `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features.length`,
                                              )}
                                              onClick={() => {
                                                const resultArray = moveElement(get(
                                                  values,
                                                  `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features`,
                                                ), indexFeature, 'down')
                                                setFieldValue(
                                                  `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features`,
                                                  resultArray,
                                                );
                                              }}
                                            >
                                              <ArrowCircleDown />
                                            </IconButton>

                                            <IconButton
                                              title="Remove"
                                              onClick={() => {
                                                if (
                                                  isValidFeatureItem(
                                                    get(
                                                      values,
                                                      `${keyField}Temp.${currentPageIndex}.slots[${indexSlot}].features[${indexFeature}]`,
                                                    ),
                                                  )
                                                ) {
                                                  handleClickOpenConfirm(
                                                    removeFeature,
                                                    indexFeature,
                                                  );
                                                } else {
                                                  removeFeature(indexFeature);
                                                }
                                              }}
                                            >
                                              <Close />
                                            </IconButton>
                                          </Box>
                                        )}
                                    </Box>
                                  </Box>
                                ))
                              ) : (
                                <Box
                                  sx={{
                                    flexDirection: 'column',
                                    minHeight: 75,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography
                                    sx={{ marginTop: 2 }}
                                    variant='caption'
                                  >
                                    No item displayed
                                  </Typography>
                                </Box>
                              )}

                              <Button
                                startIcon={<Add />}
                                onClick={() =>
                                  pushFeature(cloneDeep(DEFAULT_FEATURE_ITEM_MODEL))
                                }
                              >
                                Add feature
                              </Button>
                            </>
                          )}
                        </FieldArray>
                      </Box>
                    ))
                  ) : (
                    <Box
                      sx={{
                        flexDirection: 'column',
                        minHeight: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <NoItem />
                      <Typography sx={{ marginTop: 2 }} variant='caption'>
                        No item displayed
                      </Typography>
                    </Box>
                  )}

                  {getListSlotsByPage(
                    get(values, `${keyField}Temp.${currentPageIndex}.matchRegex`),
                    get(values, `${keyField}Temp.${currentPageIndex}.slots`),
                  )?.length > 0 && (
                      <Button
                        startIcon={<Add />}
                        onClick={handleClickOpenPopover}
                        sx={{ marginTop: 2 }}
                      >
                        Add slot
                      </Button>
                    )}

                  <Popover
                    id={id}
                    open={openPopover}
                    anchorEl={anchorEl}
                    onClose={handleClosePopover}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 16,
                        p: 2,
                      }}
                    >
                      <Autocomplete
                        onChange={(event, value) => {
                          const newSlot = {
                            ...value,
                            features: [cloneDeep(DEFAULT_FEATURE_ITEM_MODEL)],
                          };
                          pushSlot(newSlot);
                          handleClosePopover();
                        }}
                        sx={{ width: 300 }}
                        options={getListSlotsByPage(
                          get(
                            values,
                            `${keyField}Temp.${currentPageIndex}.matchRegex`,
                          ),
                          get(
                            values,
                            `${keyField}Temp.${currentPageIndex}.slots`,
                          ),
                        )}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size='small'
                            placeholder='Search slots'
                          />
                        )}
                        isOptionEqualToValue={(option, value) =>
                          option?.type === value?.type
                        }
                      ></Autocomplete>
                    </Box>
                  </Popover>
                </>
              )}
            </FieldArray>

            <Box
              sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}
            >
              <FieldArray name={keyField}>
                {({ remove: removePage }) => (
                  <>
                    <Button
                      sx={{ visibility: 'hidden' }}
                      onClick={() => {
                        const mainFunction = (indexRemove) => {
                          removePage(indexRemove);
                          handleCloseModal();
                        };
                        handleClickOpenConfirm(mainFunction, currentPageIndex);
                      }}
                      size='small'
                      color='error'
                    >
                      Delete
                    </Button>
                  </>
                )}
              </FieldArray>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  onClick={() => {
                    handleCloseModal();
                  }}
                  size='small'
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setFieldValue(keyField, values[`${keyField}Temp`]);
                    handleCloseModal();
                  }}
                  variant='contained'
                  size='small'
                >
                  Done
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </AppGridContainer>
    </>
  );
};

function FeatureConfigurationHeader({
  values,
  pageItemObj,
  currentListPagesAvailable,
  keyField = 'PageSlots'
}) {
  return (
    <Grid item xs={12} sm={12} md={12}>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          '& .MuiBox-root': {
            width: '100%',
          },
        }}
      >
        {values[keyField]?.length > 0 ? (
          <FieldArray name={keyField}>
            {({ push: pushPage }) => (
              <PageSelector
                header={
                  <Typography variant='h5' gutterBottom>
                    UI Embedding
                    <AppGlossary title=' Customize and manage additional features for this specific page.' />
                  </Typography>
                }
                options={currentListPagesAvailable}
                onChange={(value) => {
                  pushPage({
                    ...pageItemObj,
                    matchRegex: value.matchRegex,
                  });
                }}
              />
            )}
          </FieldArray>
        ) : (
          <Typography variant='h5' gutterBottom>
            UI Embedding
            <AppGlossary title=' Customize and manage additional features for this specific page.' />
          </Typography>
        )}
      </Box>
    </Grid>
  );
}

export default React.memo(FeatureStepWebMarketPlace);
